import React from 'react';
import {
  Menu,
  MenuItem,
  styled,
  InputBase,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchingProfile,
  profileSelector,
} from '@/store/profile/profileSlices';
import { UserRole } from '@/types';
import UIAvatarImage from '@/components/shared/UIAvatarImage';
import UIBell from '../UIBell';
import './UITopBar.scss';

const SearchInput = styled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-input': {
    padding: '12px 20px',
    transition: theme.transitions.create('width'),
    width: '100%',
    fontSize: '14px',
    '&::placeholder': {
      color: '#919EAB',
      opacity: 1,
    },
  },
}));

const UITopBar: React.FC = () => {
  const auth = useAuth();
  const { role } = useAuth();
  const dispatch = useDispatch();
  const profile = useSelector(profileSelector);
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = React.useState('');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  React.useEffect(() => {
    dispatch(fetchingProfile());
  }, [dispatch]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    console.log('Search for:', searchValue);
  };

  const handleAvatarClick = () => {
    const avatarElement = document.querySelector('.ui-topbar__right-avatar') as HTMLElement;
    setAnchorEl(avatarElement);
  };

  const getRoleEmoji = () => {
    switch (role) {
      case UserRole.OWNER:
        return '👑';
      case UserRole.MANAGER:
        return '⚡';
      case UserRole.TENANT:
        return '🏠';
      default:
        return '';
    }
  };

  const getRoleDisplayName = () => {
    switch (role) {
      case UserRole.OWNER:
        return 'Owner';
      case UserRole.MANAGER:
        return 'Manager';
      case UserRole.TENANT:
        return 'Tenant';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="ui-topbar">
      <div className="ui-topbar__search">
        <form onSubmit={handleSearchSubmit} className="ui-topbar__search-form">
          <div className="ui-topbar__search-container">
            <i className="fa-solid fa-magnifying-glass ui-topbar__search-icon"></i>
            <SearchInput
              placeholder="Search"
              value={searchValue}
              onChange={handleSearchChange}
              className="ui-topbar__search-input"
            />
          </div>
        </form>
      </div>

      <div className="ui-topbar__right">
        <UIBell notificationsCount={profile?.unreadNotificationsCount || 0} role={role} />

        <div className="ui-topbar__right-avatar">
          <UIAvatarImage
            avatar={profile?.avatar}
            size="medium"
            onClick={handleAvatarClick}
            alt={`${profile?.firstname || ''} ${profile?.lastname || ''}`.trim() || 'User avatar'}
          />
        </div>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <MenuItem
            onClick={() => {
              handleClose();
              navigate('/dashboard/account');
            }}
            autoFocus={false}
          >
            Account
          </MenuItem>
          <MenuItem disabled>
            {getRoleEmoji()} Role: {getRoleDisplayName()}
          </MenuItem>
          <MenuItem onClick={auth.logout} autoFocus={false}>
            Logout
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

export default UITopBar;